import { supabase } from '@/integrations/supabase/client';
import type { PayrollRun } from './types';

export interface PayrollRunWithDetails extends PayrollRun {
  creator_name?: string;
  rollback_user_name?: string;
  payslips_count?: number;
}

export interface PayrollRunFilters {
  status?: string;
  startDate?: string;
  endDate?: string;
  createdBy?: string;
}

export const payrollHistoryService = {
  /**
   * Fetch all payroll runs with optional filters
   */
  async getPayrollRuns(filters: PayrollRunFilters = {}): Promise<PayrollRunWithDetails[]> {
    try {
      let query = supabase
        .from('payroll_runs')
        .select(`
          *,
          creator:profiles!payroll_runs_created_by_fkey(first_name, last_name),
          rollback_user:profiles!payroll_runs_rolled_back_by_fkey(first_name, last_name)
        `)
        .order('created_at', { ascending: false });

      if (filters.status) {
        query = query.eq('status', filters.status);
      }

      if (filters.startDate) {
        query = query.gte('start_date', filters.startDate);
      }

      if (filters.endDate) {
        query = query.lte('end_date', filters.endDate);
      }

      if (filters.createdBy) {
        query = query.eq('created_by', filters.createdBy);
      }

      const { data, error } = await query;

      if (error) throw error;

      return (data || []).map((run: any) => ({
        ...run,
        creator_name: run.creator 
          ? `${run.creator.first_name} ${run.creator.last_name}`.trim()
          : undefined,
        rollback_user_name: run.rollback_user
          ? `${run.rollback_user.first_name} ${run.rollback_user.last_name}`.trim()
          : undefined,
      }));
    } catch (error) {
      console.error('Error fetching payroll runs:', error);
      throw error;
    }
  },

  /**
   * Fetch a single payroll run with detailed information
   */
  async getPayrollRunDetails(runId: string): Promise<PayrollRunWithDetails | null> {
    try {
      const { data, error } = await supabase
        .from('payroll_runs')
        .select(`
          *,
          creator:profiles!payroll_runs_created_by_fkey(first_name, last_name, email),
          rollback_user:profiles!payroll_runs_rolled_back_by_fkey(first_name, last_name, email)
        `)
        .eq('id', runId)
        .single();

      if (error) throw error;
      if (!data) return null;

      // Get payslips count for this run
      const { count: payslipsCount } = await supabase
        .from('payslips')
        .select('*', { count: 'exact', head: true })
        .eq('payroll_run_id', runId);

      return {
        ...data,
        creator_name: data.creator 
          ? `${data.creator.first_name} ${data.creator.last_name}`.trim()
          : undefined,
        rollback_user_name: data.rollback_user
          ? `${data.rollback_user.first_name} ${data.rollback_user.last_name}`.trim()
          : undefined,
        payslips_count: payslipsCount || 0,
      };
    } catch (error) {
      console.error('Error fetching payroll run details:', error);
      throw error;
    }
  },

  /**
   * Get payslips for a specific payroll run
   */
  async getRunPayslips(runId: string) {
    try {
      const { data, error } = await supabase
        .from('payslips')
        .select(`
          *,
          profile:profile_id(
            first_name,
            last_name,
            email,
            role
          )
        `)
        .eq('payroll_run_id', runId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return (data || []).map((payslip: any) => ({
        ...payslip,
        employee_name: payslip.profile 
          ? `${payslip.profile.first_name} ${payslip.profile.last_name}`.trim()
          : 'Unknown',
        employee_email: payslip.profile?.email,
        employee_role: payslip.profile?.role,
      }));
    } catch (error) {
      console.error('Error fetching run payslips:', error);
      throw error;
    }
  },
};
